import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { Search, Package, Filter, User, CheckCircle, ChevronRight, ChevronDown, List, RefreshCw, ClipboardList, Layers, X, AlertTriangle, Scale, Tag } from 'lucide-react';
import { pickingService } from '../../../../services/pickingService';
import { filterGroupService } from '../../../../services/filterGroupService';
import { PListDto, PListLineDto, ValidateOperatorBatchResponse } from '../../../../types/picking';
import { FilterGroupDto } from '../../../../types/filterGroup';
import { PreviewPickingModal } from './PreviewPickingModal';
import { AggregatedItemsModal } from './modals/AggregatedItemsModal';
import { OperatorBatchConfirmModal } from './modals/OperatorBatchConfirmModal';
import { PickingCard } from './PickingCard';
import { BackButton } from '../../../common/BackButton';

interface ModernPickingListProps {
  pickingLists: PListDto[];
  onSelectPicking: (pickingList: PListDto) => Promise<void>;
  onNextPicking: () => Promise<void>;
  onRefreshLists: () => void;
  onBack?: () => void;
  onBatchCreated?: (newPListId: number) => void;
  allowOperatorBatchPicking?: boolean;
}

export const ModernPickingList: React.FC<ModernPickingListProps> = ({
  pickingLists,
  onSelectPicking,
  onNextPicking,
  onRefreshLists,
  onBack,
  onBatchCreated,
  allowOperatorBatchPicking = false
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilterGroup, setSelectedFilterGroup] = useState('');
  const [filterGroups, setFilterGroups] = useState<FilterGroupDto[]>([]);
  const [loadingFilterGroups, setLoadingFilterGroups] = useState(false);
  const [loadingNextPick, setLoadingNextPick] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewPListId, setPreviewPListId] = useState<number | null>(null);
  const [isMyPicksExpanded, setIsMyPicksExpanded] = useState(false);
  const [showAggregatedItemsModal, setShowAggregatedItemsModal] = useState(false);

  // Batch selection mode state
  const [batchSelectMode, setBatchSelectMode] = useState(false);
  const [selectedForBatch, setSelectedForBatch] = useState<Set<number>>(new Set());
  const [batchValidation, setBatchValidation] = useState<ValidateOperatorBatchResponse | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [showBatchConfirmModal, setShowBatchConfirmModal] = useState(false);
  const validationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Dynamic batch filter state: { fieldKey: selectedValue }
  const [batchFilters, setBatchFilters] = useState<Record<string, string>>({});

  // Cached line data for batch filters + accordion reuse
  const [batchLineData, setBatchLineData] = useState<Map<number, PListLineDto[]>>(new Map());
  const [loadingLineData, setLoadingLineData] = useState(false);

  // Load filter groups on component mount
  useEffect(() => {
    const loadFilterGroups = async () => {
      try {
        setLoadingFilterGroups(true);
        const groups = await filterGroupService.getCurrent();
        setFilterGroups(groups);
      } catch (err) {
        console.error('Error loading filter groups:', err);
      } finally {
        setLoadingFilterGroups(false);
      }
    };

    loadFilterGroups();
  }, []);

  // Debounced validation when batch selection changes
  useEffect(() => {
    if (!batchSelectMode || selectedForBatch.size < 2) {
      setBatchValidation(null);
      return;
    }

    if (validationTimerRef.current) {
      clearTimeout(validationTimerRef.current);
    }

    validationTimerRef.current = setTimeout(async () => {
      try {
        setIsValidating(true);
        const result = await pickingService.validateOperatorBatch(Array.from(selectedForBatch));
        setBatchValidation(result);
      } catch (err) {
        console.error('Error validating batch:', err);
        setBatchValidation(null);
      } finally {
        setIsValidating(false);
      }
    }, 800);

    return () => {
      if (validationTimerRef.current) {
        clearTimeout(validationTimerRef.current);
      }
    };
  }, [batchSelectMode, selectedForBatch]);

  // Optimized callback functions with useCallback to prevent unnecessary re-renders
  const handleLockPickingList = useCallback(async (pListId: number) => {
    try {
      await pickingService.lockPickingList(pListId);
      onRefreshLists();
    } catch (err) {
      console.error('Error locking picking list:', err);
      alert(t('warehouse.modules.picking.errors.lockFailed'));
    }
  }, [onRefreshLists]);

  const handleUnlockPickingList = useCallback(async (pListId: number) => {
    try {
      await pickingService.unlockPickingList(pListId);
      onRefreshLists();
    } catch (err) {
      console.error('Error unlocking picking list:', err);
      alert(t('warehouse.modules.picking.errors.unlockFailed'));
    }
  }, [onRefreshLists]);

  const handleStartPicking = useCallback(async (pickingList: PListDto) => {
    try {
      await onSelectPicking(pickingList);
    } catch (err) {
      console.error('Error starting picking:', err);
    }
  }, [onSelectPicking]);

  const handleNextPicking = async () => {
    try {
      setLoadingNextPick(true);
      await onNextPicking();
    } catch (err) {
      console.error('Error starting next picking:', err);
    } finally {
      setLoadingNextPick(false);
    }
  };

  const handlePreviewPicking = useCallback((pListId: number) => {
    setPreviewPListId(pListId);
    setShowPreviewModal(true);
  }, []);

  const handleClosePreview = useCallback(() => {
    setShowPreviewModal(false);
    setPreviewPListId(null);
  }, []);

  // Pre-fetch line data for all visible PLists (for filters + accordion)
  const fetchBatchLineData = async (lists: PListDto[]) => {
    setLoadingLineData(true);
    const lineMap = new Map<number, PListLineDto[]>();
    try {
      const results = await Promise.allSettled(
        lists.map(list => pickingService.getPickingDetails(list.pListID))
      );
      results.forEach((result, idx) => {
        if (result.status === 'fulfilled' && result.value.lines) {
          lineMap.set(lists[idx].pListID, result.value.lines);
        }
      });
    } catch {
      // Partial data is fine — filters degrade gracefully
    }
    setBatchLineData(lineMap);
    setLoadingLineData(false);
  };

  // Batch selection handlers
  const handleEnterBatchMode = () => {
    setBatchSelectMode(true);
    setSelectedForBatch(new Set());
    setBatchValidation(null);
    // Pre-fetch line data for all selectable PLists
    const allLists = [...pickingLists];
    fetchBatchLineData(allLists);
  };

  const handleExitBatchMode = async () => {
    // Unlock all selected PLists
    for (const pListId of Array.from(selectedForBatch)) {
      try {
        await pickingService.unlockPickingList(pListId);
      } catch (err) {
        console.error(`Error unlocking PList ${pListId}:`, err);
      }
    }
    setBatchSelectMode(false);
    setSelectedForBatch(new Set());
    setBatchValidation(null);
    setBatchFilters({});
    setBatchLineData(new Map());
    onRefreshLists();
  };

  const handleToggleBatchSelection = useCallback(async (pListId: number) => {
    setSelectedForBatch(prev => {
      const next = new Set(prev);
      if (next.has(pListId)) {
        next.delete(pListId);
        pickingService.unlockPickingList(pListId).catch((err) => {
          console.error(`Failed to unlock PList ${pListId}:`, err);
          // Re-add to selection since unlock failed
          setSelectedForBatch(p => new Set(p).add(pListId));
        });
      } else {
        next.add(pListId);
        pickingService.lockPickingList(pListId).catch((err) => {
          console.error(`Failed to lock PList ${pListId}:`, err);
          // Remove from selection since lock failed
          setSelectedForBatch(p => {
            const reverted = new Set(p);
            reverted.delete(pListId);
            return reverted;
          });
        });
      }
      return next;
    });
  }, []);

  const handleBatchCreated = (newPListId: number) => {
    setBatchSelectMode(false);
    setSelectedForBatch(new Set());
    setBatchValidation(null);
    setShowBatchConfirmModal(false);
    setBatchFilters({});
    setBatchLineData(new Map());
    if (onBatchCreated) {
      onBatchCreated(newPListId);
    }
  };

  // Sorting function
  const sortPickingLists = (lists: PListDto[]): PListDto[] => {
    return [...lists].sort((a, b) => {
      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }
      return a.pListID - b.pListID;
    });
  };

  // Split picking lists based on LockedBy status
  const myPickingLists = sortPickingLists(
    pickingLists.filter(list =>
      list.lockedBy && list.lockedBy.trim() !== ''
    )
  );

  const availablePickingLists = sortPickingLists(
    pickingLists.filter(list =>
      !list.lockedBy || list.lockedBy.trim() === ''
    )
  );

  // Filter based on search and filter group
  const filterList = (list: PListDto) => {
    const matchesSearch =
      list.description1?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      list.description2?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      list.companyNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      list.pListID.toString().includes(searchTerm);

    const matchesFilterGroup =
      !selectedFilterGroup ||
      list.filterGroupID === selectedFilterGroup;

    return matchesSearch && matchesFilterGroup;
  };

  const filteredMyLists = myPickingLists.filter(filterList);
  const filteredAvailableLists = availablePickingLists.filter(filterList);

  // Determine if a PList is selectable for batch
  const isBatchSelectable = (list: PListDto): { selectable: boolean; reason?: string } => {
    if (list.pListIsBatchYesNo) return { selectable: false, reason: 'Allerede et samleplukk' };
    if (list.pListStatus !== 10) return { selectable: false, reason: 'Ikke godkjent' };
    if (list.lockedBy && list.lockedBy.trim() !== '' && !selectedForBatch.has(list.pListID)) {
      return { selectable: false, reason: 'Låst av annen operatør' };
    }
    return { selectable: true };
  };

  // === Dynamic batch filter chips ===
  // Auto-generated from data: only shown when a field has >1 distinct value
  interface BatchFilterOption {
    key: string;
    label: string;
    values: { value: string; label: string; count: number }[];
  }

  const batchFilterOptions = useMemo((): BatchFilterOption[] => {
    if (!batchSelectMode) return [];

    const allLists = [...filteredMyLists, ...filteredAvailableLists];
    if (allLists.length < 2) return [];

    const filters: BatchFilterOption[] = [];

    // Helper: collect unique values for a field
    const collectValues = (
      fieldKey: keyof PListDto,
      label: string,
      formatValue?: (v: unknown) => string | null
    ) => {
      const valueMap = new Map<string, number>();
      for (const list of allLists) {
        const raw = list[fieldKey];
        const displayVal = formatValue ? formatValue(raw) : (raw != null && raw !== '' ? String(raw) : null);
        if (displayVal) {
          valueMap.set(displayVal, (valueMap.get(displayVal) || 0) + 1);
        }
      }
      // Only show chip if >1 unique value (otherwise filter is useless)
      if (valueMap.size > 1) {
        const values = Array.from(valueMap.entries())
          .map(([value, count]) => ({ value, label: value, count }))
          .sort((a, b) => b.count - a.count);
        filters.push({ key: fieldKey as string, label, values });
      }
    };

    // Carrier / delivery method
    collectValues('carrierName', 'Transportør');
    collectValues('deliveryMethodNo', 'Leveringsmetode', v => v != null ? `Metode ${v}` : null);
    collectValues('packZoneID', 'Pakkesone');

    // Delivery date — format nicely
    collectValues('deliveryDate', 'Leveringsdato', v => {
      if (!v) return null;
      try {
        const d = new Date(v as string);
        return d.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit' });
      } catch { return null; }
    });

    // Weight buckets
    const weightBuckets = new Map<string, number>();
    for (const list of allLists) {
      const w = list.weight || list.totalWeight || 0;
      const bucket = w === 0 ? null : w <= 5 ? '0-5 kg' : w <= 20 ? '5-20 kg' : '20+ kg';
      if (bucket) weightBuckets.set(bucket, (weightBuckets.get(bucket) || 0) + 1);
    }
    if (weightBuckets.size > 1) {
      filters.push({
        key: '_weightBucket',
        label: 'Vekt',
        values: Array.from(weightBuckets.entries())
          .map(([value, count]) => ({ value, label: value, count }))
          .sort((a, b) => a.value.localeCompare(b.value))
      });
    }

    // Line count buckets
    const lineBuckets = new Map<string, number>();
    for (const list of allLists) {
      const n = list.numOfOrderLines || 0;
      const bucket = n <= 2 ? '1-2 linjer' : n <= 5 ? '3-5 linjer' : '6+ linjer';
      lineBuckets.set(bucket, (lineBuckets.get(bucket) || 0) + 1);
    }
    if (lineBuckets.size > 1) {
      filters.push({
        key: '_lineBucket',
        label: 'Linjer',
        values: Array.from(lineBuckets.entries())
          .map(([value, count]) => ({ value, label: value, count }))
          .sort((a, b) => a.value.localeCompare(b.value))
      });
    }

    // Priority
    collectValues('priority', 'Prioritet', v => v != null ? `P:${v}` : null);

    // Filter group
    collectValues('filterGroupID', 'Filtergruppe');

    // === Line-level filters (from pre-fetched batchLineData) ===
    if (batchLineData.size > 0) {
      // Kunde: extract from description1 (remove "Order " prefix if present)
      const kundeMap = new Map<string, number>();
      for (const list of allLists) {
        let kunde = list.description1 || '';
        // Remove common prefixes like "Order ", "Ordre " etc.
        kunde = kunde.replace(/^(Order|Ordre)\s+/i, '').trim();
        if (kunde) {
          kundeMap.set(kunde, (kundeMap.get(kunde) || 0) + 1);
        }
      }
      if (kundeMap.size > 1) {
        filters.push({
          key: '_kunde',
          label: 'Kunde',
          values: Array.from(kundeMap.entries())
            .map(([value, count]) => ({ value, label: value, count }))
            .sort((a, b) => b.count - a.count)
        });
      }

      // Totalt antall: sum(lines.qty) per PList, bucketed
      const qtyBuckets = new Map<string, number>();
      for (const list of allLists) {
        const lines = batchLineData.get(list.pListID);
        if (lines) {
          const totalQty = lines.reduce((sum, l) => sum + l.qty, 0);
          const bucket = totalQty <= 10 ? '1-10 stk' : totalQty <= 50 ? '11-50 stk' : '50+ stk';
          qtyBuckets.set(bucket, (qtyBuckets.get(bucket) || 0) + 1);
        }
      }
      if (qtyBuckets.size > 1) {
        filters.push({
          key: '_qtyBucket',
          label: 'Totalt antall',
          values: Array.from(qtyBuckets.entries())
            .map(([value, count]) => ({ value, label: value, count }))
            .sort((a, b) => a.value.localeCompare(b.value))
        });
      }

      // Lagerområde: BinCode prefix (first 2 chars = rack/aisle)
      const areaMap = new Map<string, number>();
      for (const list of allLists) {
        const lines = batchLineData.get(list.pListID);
        if (lines) {
          const areas = new Set<string>();
          for (const line of lines) {
            if (line.binCode && line.binCode.length >= 2) {
              areas.add(line.binCode.substring(0, 2));
            }
          }
          for (const area of Array.from(areas)) {
            areaMap.set(area, (areaMap.get(area) || 0) + 1);
          }
        }
      }
      if (areaMap.size > 1) {
        filters.push({
          key: '_lagerområde',
          label: 'Lagerområde',
          values: Array.from(areaMap.entries())
            .map(([value, count]) => ({ value, label: `Reol ${value}`, count }))
            .sort((a, b) => a.value.localeCompare(b.value))
        });
      }

      // Varegruppe: from PListLineDto[].orderLineItemGroup1
      const varegruppeMap = new Map<string, number>();
      for (const list of allLists) {
        const lines = batchLineData.get(list.pListID);
        if (lines) {
          const groups = new Set<string>();
          for (const line of lines) {
            if (line.orderLineItemGroup1 && line.orderLineItemGroup1.trim()) {
              groups.add(line.orderLineItemGroup1.trim());
            }
          }
          for (const group of Array.from(groups)) {
            varegruppeMap.set(group, (varegruppeMap.get(group) || 0) + 1);
          }
        }
      }
      if (varegruppeMap.size > 1) {
        filters.push({
          key: '_varegruppe',
          label: 'Varegruppe',
          values: Array.from(varegruppeMap.entries())
            .map(([value, count]) => ({ value, label: value, count }))
            .sort((a, b) => b.count - a.count)
        });
      }
    }

    return filters;
  }, [batchSelectMode, filteredMyLists, filteredAvailableLists, batchLineData]);

  // Apply batch filters to lists
  const applyBatchFilters = (lists: PListDto[]): PListDto[] => {
    if (Object.keys(batchFilters).length === 0) return lists;
    return lists.filter(list => {
      for (const [filterKey, filterValue] of Object.entries(batchFilters)) {
        // Special bucket filters
        if (filterKey === '_weightBucket') {
          const w = list.weight || list.totalWeight || 0;
          const bucket = w === 0 ? null : w <= 5 ? '0-5 kg' : w <= 20 ? '5-20 kg' : '20+ kg';
          if (bucket !== filterValue) return false;
          continue;
        }
        if (filterKey === '_lineBucket') {
          const n = list.numOfOrderLines || 0;
          const bucket = n <= 2 ? '1-2 linjer' : n <= 5 ? '3-5 linjer' : '6+ linjer';
          if (bucket !== filterValue) return false;
          continue;
        }
        // Kunde — compare with description1 (stripped prefix)
        if (filterKey === '_kunde') {
          let kunde = list.description1 || '';
          kunde = kunde.replace(/^(Order|Ordre)\s+/i, '').trim();
          if (kunde !== filterValue) return false;
          continue;
        }
        // Totalt antall — line-level qty bucket
        if (filterKey === '_qtyBucket') {
          const lines = batchLineData.get(list.pListID);
          if (!lines) return false;
          const totalQty = lines.reduce((sum, l) => sum + l.qty, 0);
          const bucket = totalQty <= 10 ? '1-10 stk' : totalQty <= 50 ? '11-50 stk' : '50+ stk';
          if (bucket !== filterValue) return false;
          continue;
        }
        // Lagerområde — match if PList has at least one line from selected area
        if (filterKey === '_lagerområde') {
          const lines = batchLineData.get(list.pListID);
          if (!lines) return false;
          const hasArea = lines.some(l => l.binCode && l.binCode.length >= 2 && l.binCode.substring(0, 2) === filterValue);
          if (!hasArea) return false;
          continue;
        }
        // Varegruppe — match if PList has at least one line from selected group
        if (filterKey === '_varegruppe') {
          const lines = batchLineData.get(list.pListID);
          if (!lines) return false;
          const hasGroup = lines.some(l => l.orderLineItemGroup1 && l.orderLineItemGroup1.trim() === filterValue);
          if (!hasGroup) return false;
          continue;
        }
        // Delivery date — compare formatted
        if (filterKey === 'deliveryDate') {
          try {
            const d = list.deliveryDate ? new Date(list.deliveryDate).toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit' }) : null;
            if (d !== filterValue) return false;
          } catch { return false; }
          continue;
        }
        // DeliveryMethodNo — compare formatted
        if (filterKey === 'deliveryMethodNo') {
          const val = list.deliveryMethodNo != null ? `Metode ${list.deliveryMethodNo}` : null;
          if (val !== filterValue) return false;
          continue;
        }
        // Priority — compare formatted
        if (filterKey === 'priority') {
          const val = `P:${list.priority}`;
          if (val !== filterValue) return false;
          continue;
        }
        // Standard string field comparison
        const listValue = (list as unknown as Record<string, unknown>)[filterKey];
        if (String(listValue || '') !== filterValue) return false;
      }
      return true;
    });
  };

  const toggleBatchFilter = (key: string, value: string) => {
    setBatchFilters(prev => {
      const next = { ...prev };
      if (next[key] === value) {
        delete next[key]; // Toggle off
      } else {
        next[key] = value; // Set filter
      }
      return next;
    });
  };

  // Render picking cards with batch mode support
  const renderPickingSection = (
    title: string,
    lists: PListDto[],
    isOwnedSection: boolean,
    icon: React.ReactNode,
    isCollapsible: boolean = false,
    isExpanded: boolean = true,
    onToggleExpand?: () => void
  ) => {
    if (lists.length === 0) return null;

    return (
      <div className="mb-8">
        <div
          className={`text-xl font-bold text-gray-900 mb-4 flex items-center gap-2 ${
            isCollapsible ? 'cursor-pointer hover:text-blue-600 transition-colors' : ''
          }`}
          onClick={isCollapsible ? onToggleExpand : undefined}
        >
          {isCollapsible && (
            isExpanded ? (
              <ChevronDown className="text-gray-600" size={24} />
            ) : (
              <ChevronRight className="text-gray-600" size={24} />
            )
          )}
          {icon}
          {title} ({lists.length})
        </div>
        {isExpanded && (
          <div className="grid gap-3">
            {lists.map((list) => {
              const batchInfo = batchSelectMode ? isBatchSelectable(list) : { selectable: true };
              return (
                <PickingCard
                  key={list.pListID}
                  list={list}
                  isOwned={isOwnedSection || selectedForBatch.has(list.pListID)}
                  onStartPicking={handleStartPicking}
                  onLockList={handleLockPickingList}
                  onUnlockList={handleUnlockPickingList}
                  onPreviewList={handlePreviewPicking}
                  batchSelectMode={batchSelectMode}
                  isSelectedForBatch={selectedForBatch.has(list.pListID)}
                  isBatchSelectable={batchInfo.selectable}
                  batchSelectDisabledReason={batchInfo.reason}
                  onToggleBatchSelection={handleToggleBatchSelection}
                  cachedLines={batchSelectMode ? batchLineData.get(list.pListID) : undefined}
                />
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // All lists combined for batch mode (no section split), with batch filters applied
  const allFilteredLists = batchSelectMode
    ? applyBatchFilters(sortPickingLists([...filteredMyLists, ...filteredAvailableLists]))
    : [];

  return (
    <div className={`min-h-screen bg-gray-50 p-2 sm:p-4 ${batchSelectMode && selectedForBatch.size > 0 ? 'pb-48' : ''}`}>
      <div className="max-w-7xl mx-auto">
        <div className={`bg-white rounded-lg shadow-sm p-3 sm:p-4 mb-4 sm:mb-6 ${batchSelectMode ? 'border-2 border-[#F58220] bg-orange-50' : ''}`}>
          {/* Header with title and search field */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
            <div className="flex items-center gap-3">
              {onBack && !batchSelectMode && <BackButton onClick={onBack} />}
              <h1 className="text-lg sm:text-xl font-bold text-gray-900">
                {batchSelectMode ? 'Velg plukklister for samleplukk' : t('warehouse.modules.picking.sections.newPickingLists')}
              </h1>
            </div>
            <div className="relative flex-1 sm:max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder={t('warehouse.modules.picking.placeholders.searchPickingLists')}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm min-h-[44px]"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Filter group dropdown */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
            <label className="text-base sm:text-xl font-bold text-gray-900 sm:w-40">
              {t('warehouse.modules.picking.labels.filterGroup')}
            </label>
            <div className="relative flex-1 sm:max-w-md">
              <List className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <select
                value={selectedFilterGroup}
                onChange={(e) => setSelectedFilterGroup(e.target.value)}
                disabled={loadingFilterGroups}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm min-h-[44px] bg-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">{t('warehouse.modules.picking.labels.allFilterGroups')}</option>
                {filterGroups.map((group) => (
                  <option key={group.filterGroupID} value={group.filterGroupID}>
                    {group.filterGroupDescr}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Buttons row */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
            {batchSelectMode ? (
              <>
                <button
                  onClick={handleExitBatchMode}
                  className="flex items-center justify-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors min-h-[44px] w-full sm:w-auto"
                >
                  <X size={18} />
                  Avslutt valg
                </button>
                <button
                  onClick={() => setShowBatchConfirmModal(true)}
                  disabled={selectedForBatch.size < 2 || (batchValidation !== null && !batchValidation.isValid)}
                  className="flex items-center justify-center gap-2 bg-[#F58220] text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] w-full sm:w-auto"
                >
                  <Layers size={18} />
                  Opprett samleplukk ({selectedForBatch.size})
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleNextPicking}
                  disabled={loadingNextPick}
                  className="flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 whitespace-nowrap min-h-[44px] w-full sm:w-auto"
                >
                  {loadingNextPick ? (
                    <>
                      <div className="w-4 h-4 animate-spin border-2 border-white border-t-transparent rounded-full" />
                      {t('warehouse.modules.picking.actions.starting')}
                    </>
                  ) : (
                    <>
                      <Package size={18} />
                      {t('warehouse.modules.picking.actions.nextPicking')}
                    </>
                  )}
                </button>

                <button
                  onClick={onRefreshLists}
                  className="flex items-center justify-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors min-h-[44px] w-full sm:w-auto"
                >
                  <RefreshCw size={18} />
                  {t('common.buttons.update')}
                </button>

                <button
                  onClick={() => setShowAggregatedItemsModal(true)}
                  className="flex items-center justify-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-lg hover:bg-amber-200 transition-colors min-h-[44px] w-full sm:w-auto"
                  title="Vis sammenstilling av alle varer fra godkjente plukklister"
                >
                  <ClipboardList size={18} />
                  Samleplukk
                </button>

                {allowOperatorBatchPicking && (
                  <button
                    onClick={handleEnterBatchMode}
                    className="flex items-center justify-center gap-2 bg-[#F58220] text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors min-h-[44px] w-full sm:w-auto"
                  >
                    <Layers size={18} />
                    Opprett samleplukk
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* In batch mode: filter chips + lists */}
        {batchSelectMode ? (
          <div className="mb-8">
            {/* Loading indicator for line data pre-fetch */}
            {loadingLineData && (
              <div className="mb-3 flex items-center gap-2 text-xs text-gray-500">
                <div className="w-3 h-3 animate-spin border border-gray-400 border-t-transparent rounded-full" />
                Laster linjedata for filtrering...
              </div>
            )}

            {/* Dynamic filter chips — auto-generated from data */}
            {batchFilterOptions.length > 0 && (
              <div className="mb-4 space-y-2">
                {batchFilterOptions.map(filterOpt => (
                  <div key={filterOpt.key} className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium text-gray-500 w-24 sm:w-auto flex-shrink-0">{filterOpt.label}:</span>
                    <div className="flex flex-wrap gap-1.5">
                      {filterOpt.values.map(v => {
                        const isActive = batchFilters[filterOpt.key] === v.value;
                        return (
                          <button
                            key={v.value}
                            onClick={() => toggleBatchFilter(filterOpt.key, v.value)}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors min-h-[28px] ${
                              isActive
                                ? 'bg-[#F58220] text-white shadow-sm'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {v.label}
                            <span className={`text-[10px] ${isActive ? 'text-orange-100' : 'text-gray-400'}`}>({v.count})</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {Object.keys(batchFilters).length > 0 && (
                  <button
                    onClick={() => setBatchFilters({})}
                    className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mt-1"
                  >
                    <X size={12} />
                    Fjern alle filtre
                  </button>
                )}
              </div>
            )}

            <div className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Package className="text-[#F58220]" size={24} />
              Velg plukklister ({allFilteredLists.length})
            </div>
            <div className="grid gap-3">
              {allFilteredLists.map((list) => {
                const batchInfo = isBatchSelectable(list);
                return (
                  <PickingCard
                    key={list.pListID}
                    list={list}
                    isOwned={selectedForBatch.has(list.pListID)}
                    onStartPicking={handleStartPicking}
                    onLockList={handleLockPickingList}
                    onUnlockList={handleUnlockPickingList}
                    onPreviewList={handlePreviewPicking}
                    batchSelectMode={true}
                    isSelectedForBatch={selectedForBatch.has(list.pListID)}
                    isBatchSelectable={batchInfo.selectable}
                    batchSelectDisabledReason={batchInfo.reason}
                    onToggleBatchSelection={handleToggleBatchSelection}
                    cachedLines={batchLineData.get(list.pListID)}
                  />
                );
              })}
            </div>
          </div>
        ) : (
          <>
            {/* Mine plukk-seksjonen - Collapsible */}
            {renderPickingSection(
              t('warehouse.modules.picking.sections.myPicks'),
              filteredMyLists,
              true,
              <User className="text-green-600" size={24} />,
              true,
              isMyPicksExpanded,
              () => setIsMyPicksExpanded(!isMyPicksExpanded)
            )}

            {/* Tilgjengelige plukk-seksjonen */}
            {renderPickingSection(
              t('warehouse.modules.picking.sections.availablePicks'),
              filteredAvailableLists,
              false,
              <Package className="text-blue-600" size={24} />
            )}
          </>
        )}

        {filteredAvailableLists.length === 0 && filteredMyLists.length === 0 && !batchSelectMode && (
          <div className="text-center py-8 text-gray-500">
            <Package size={48} className="mx-auto mb-4 text-gray-300" />
            <p>{t('warehouse.modules.picking.messages.noPickingListsFound')}</p>
          </div>
        )}
      </div>

      {/* Floating summary bar in batch mode */}
      {batchSelectMode && selectedForBatch.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-[#F58220] shadow-lg p-4 z-40">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <span className="font-bold text-gray-900">
                  {selectedForBatch.size} plukklister valgt
                </span>
                {isValidating && (
                  <span className="text-gray-500 flex items-center gap-1">
                    <div className="w-3 h-3 animate-spin border border-gray-400 border-t-transparent rounded-full" />
                    Validerer...
                  </span>
                )}
                {batchValidation && (
                  <>
                    <span className="text-gray-600">
                      {batchValidation.totalLines} linjer
                    </span>
                    {batchValidation.totalWeight > 0 && (
                      <span className="text-gray-600 flex items-center gap-1">
                        <Scale size={14} />
                        {batchValidation.totalWeight.toFixed(1)} kg
                      </span>
                    )}
                    {batchValidation.warnings.length > 0 && (
                      <span className="text-amber-600 flex items-center gap-1">
                        <AlertTriangle size={14} />
                        {batchValidation.warnings.length} advarsel{batchValidation.warnings.length > 1 ? 'er' : ''}
                      </span>
                    )}
                    {batchValidation.errors.length > 0 && (
                      <span className="text-red-600 font-medium">
                        {batchValidation.errors[0]}
                      </span>
                    )}
                  </>
                )}
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  onClick={handleExitBatchMode}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors min-h-[44px]"
                >
                  Avbryt
                </button>
                <button
                  onClick={() => setShowBatchConfirmModal(true)}
                  disabled={selectedForBatch.size < 2 || isValidating || (batchValidation !== null && !batchValidation.isValid)}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-[#F58220] text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                >
                  <Layers size={18} />
                  Opprett samleplukk
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      <PreviewPickingModal
        isOpen={showPreviewModal}
        pListId={previewPListId}
        onClose={handleClosePreview}
      />

      {/* Aggregated Items Modal */}
      <AggregatedItemsModal
        isOpen={showAggregatedItemsModal}
        onClose={() => setShowAggregatedItemsModal(false)}
      />

      {/* Batch Confirm Modal */}
      <OperatorBatchConfirmModal
        isOpen={showBatchConfirmModal}
        validation={batchValidation}
        selectedPListIds={Array.from(selectedForBatch)}
        onClose={() => setShowBatchConfirmModal(false)}
        onBatchCreated={handleBatchCreated}
      />
    </div>
  );
};
